// lib/playbook-funnel.ts
// Pure logic behind the GA4 Buyer's Playbook form funnel (Priority 293). No
// DOM, no network — components/public/LeadCapture.tsx owns the consent read
// and the gtag() call; everything decidable without a browser lives here so
// it is unit-testable (tests/unit/playbook-funnel.test.ts).

export const PLAYBOOK_FORM_VIEW_EVENT = "playbook_form_view";
export const PLAYBOOK_FORM_START_EVENT = "playbook_form_start";
export const PLAYBOOK_FORM_SUBMIT_EVENT = "playbook_form_submit";

/** Funnel order: view, then start, then submit. */
export const PLAYBOOK_FUNNEL_EVENTS = [
  PLAYBOOK_FORM_VIEW_EVENT,
  PLAYBOOK_FORM_START_EVENT,
  PLAYBOOK_FORM_SUBMIT_EVENT,
] as const;

export type PlaybookFunnelEvent = (typeof PLAYBOOK_FUNNEL_EVENTS)[number];

/**
 * What LeadCapture is reporting:
 * - `view` — the idle form is showing.
 * - `start` — the visitor interacted with the email field (focus or change).
 * - `success` — the JSON POST succeeded (res.ok and body.ok).
 * - `subscribed_on_load` — the page loaded already in the subscribed return
 *   state (?subscribed=1 / #subscribed=1).
 * - `error` — a failed POST, or the leadError return flag.
 */
export type PlaybookFunnelPhase =
  | "view"
  | "start"
  | "success"
  | "subscribed_on_load"
  | "error";

// The furthest funnel step each phase proves. `error` proves nothing, and
// `subscribed_on_load` is handled apart: it is submit alone, never the steps
// before it, because this page view never showed the form.
const FURTHEST_STEP: Record<
  Exclude<PlaybookFunnelPhase, "subscribed_on_load" | "error">,
  number
> = {
  view: 0,
  start: 1,
  success: 2,
};

/**
 * Which events to emit now, in funnel order. The caller adds what it actually
 * sent to its own set, so each event fires at most once per LeadCapture mount.
 *
 * `canSend` is false when cookie consent is not accepted or gtag is missing:
 * nothing is returned, so nothing gets marked sent. That is why `start` and
 * `success` also return the earlier steps still unsent — a visitor who
 * accepts cookies after the form rendered gets `view` on their next
 * interaction instead of a funnel that begins at `start`.
 *
 * Once submit has been sent the funnel is over and nothing more is emitted.
 */
export function newPlaybookFunnelEvents(
  phase: PlaybookFunnelPhase,
  alreadySent: ReadonlySet<PlaybookFunnelEvent>,
  canSend: boolean,
): PlaybookFunnelEvent[] {
  if (!canSend) return [];
  if (phase === "error") return [];
  if (alreadySent.has(PLAYBOOK_FORM_SUBMIT_EVENT)) return [];
  if (phase === "subscribed_on_load") return [PLAYBOOK_FORM_SUBMIT_EVENT];
  return PLAYBOOK_FUNNEL_EVENTS.slice(0, FURTHEST_STEP[phase] + 1).filter(
    (name) => !alreadySent.has(name),
  );
}
